import React, { useState, useEffect, useCallback } from 'react';
import ProductCard from '../../components/ProductCard';
import EmptyState from '../../components/EmptyState';
import IngredientDrawer from '../../components/IngredientDrawer';
import { supabase } from '../../lib/supabase';
import { fetchSavedProducts, unsaveProduct } from '../../lib/api/saved-products';

function ListsPage({ onNavigate }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const loadSaved = useCallback(async (uid) => {
    setLoading(true);
    setError(null);
    try {
      const saved = await fetchSavedProducts(uid);
      setProducts(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setUserId(user.id);
        loadSaved(user.id);
      } else {
        setLoading(false);
      }
    });
  }, [loadSaved]);

  async function handleUnsave(product) {
    if (!userId) return;
    await unsaveProduct(userId, product.id);
    setProducts((prev) => prev.filter((p) => p.id !== product.id));
  }

  return (
    <div className="px-6 py-10">
      <div className="max-w-content mx-auto">
        <h1 className="text-h2 text-neutral-900 mb-2">Saved Products</h1>
        <p className="text-body text-neutral-500 mb-8">
          Products you've saved for quick reference.
        </p>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-surface-card rounded-lg border border-neutral-200 p-6 flex flex-col gap-4">
                <div className="flex justify-between gap-3">
                  <div className="flex flex-col gap-2 flex-1">
                    <div className="h-3 bg-neutral-100 rounded-md animate-pulse w-1/3" />
                    <div className="h-5 bg-neutral-100 rounded-md animate-pulse w-2/3" />
                  </div>
                  <div className="h-6 w-16 bg-neutral-100 rounded-sm animate-pulse" />
                </div>
                <div className="h-5 w-24 bg-neutral-100 rounded-full animate-pulse" />
                <div className="space-y-2">
                  <div className="h-3 bg-neutral-100 rounded-md animate-pulse" />
                  <div className="h-3 bg-neutral-100 rounded-md animate-pulse w-4/5" />
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <p className="text-body text-error">Failed to load saved products: {error}</p>
        )}

        {!loading && !error && products.length === 0 && (
          <EmptyState
            title="Nothing saved yet"
            description="Browse products and tap Save to build your personal library."
            action={onNavigate ? { label: 'Browse products', onClick: () => onNavigate('home') } : undefined}
            icon={
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <path d="M12 8h24a4 4 0 014 4v26l-16-8-16 8V12a4 4 0 014-4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
          />
        )}

        {!loading && !error && products.length > 0 && (
          <>
            <p className="text-caption text-neutral-400 mb-4">
              {products.length} {products.length === 1 ? 'product' : 'products'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  saved={true}
                  onSave={handleUnsave}
                  onClick={() => setSelectedProduct(product)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {selectedProduct && (
        <IngredientDrawer
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}

export default ListsPage;
